import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { footer, nav } from '../mockData';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const { language } = useLanguage();
  const t = footer[language];
  const navT = nav[language];
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/newsletter`, { email });
      
      toast({
        title: language === 'en' ? 'Success!' : 'نجح!',
        description: language === 'en' ? 'You have been subscribed to our newsletter.' : 'تم اشتراكك في نشرتنا الإخبارية.'
      });
      
      setEmail('');
    } catch (error) {
      console.error('Newsletter error:', error);
      toast({
        title: language === 'en' ? 'Error' : 'خطأ',
        description: language === 'en' ? 'Failed to subscribe. Please try again.' : 'فشل الاشتراك. يرجى المحاولة مرة أخرى.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const navLinks = [
    { path: '/', label: navT.home },
    { path: '/about', label: navT.about },
    { path: '/program', label: navT.program },
    { path: '/submissions', label: navT.submissions },
    { path: '/jury', label: navT.jury },
    { path: '/partners', label: navT.partners },
    { path: '/contact', label: navT.contact }
  ];

  return (
    <footer className="bg-black border-t border-white/10 text-white py-16">
      <div className="container mx-auto px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Brand */}
          <div>
            <h3 className="text-3xl font-serif font-bold text-gold mb-4">CISF</h3>
            <p className="text-gray-400 text-sm leading-relaxed">{t.tagline}</p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-gold text-sm uppercase tracking-wider mb-4 font-semibold">
              {language === 'en' ? 'Quick Links' : 'روابط سريعة'}
            </h4>
            <nav className="space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block text-gray-400 hover:text-gold transition-colors text-sm"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-gold text-sm uppercase tracking-wider mb-4 font-semibold">{t.newsletter.title}</h4>
            <form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <Input
                type="email"
                placeholder={t.newsletter.placeholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="bg-white/5 border-white/20 text-white placeholder:text-gray-500"
              />
              <Button type="submit" className="w-full bg-gold text-black hover:bg-gold/90" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {language === 'en' ? 'Subscribing...' : 'جاري الاشتراك...'}
                  </>
                ) : (
                  t.newsletter.button
                )}
              </Button>
            </form>
          </div>
        </div>

        {/* Social & Copyright */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <p className="text-gray-500 text-sm">{t.rights}</p>
          <div className="flex space-x-6">
            <a href="#" className="text-gray-400 hover:text-gold transition-colors">
              <Facebook className="h-5 w-5" />
            </a>
            <a href="#" className="text-gray-400 hover:text-gold transition-colors">
              <Twitter className="h-5 w-5" />
            </a>
            <a href="#" className="text-gray-400 hover:text-gold transition-colors">
              <Instagram className="h-5 w-5" />
            </a>
            <a href="#" className="text-gray-400 hover:text-gold transition-colors">
              <Linkedin className="h-5 w-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;